import { Component, computed, EventEmitter, inject, input, Output, signal } from '@angular/core';
import { CommonModule } from '@angular/common'; 
import { ActivatedRoute, Router } from '@angular/router';
import { Sector } from '../../../../domain/sector/sector';
import { SectorListCard } from '../sector-list-card/sector-list-card';
import { SectorType } from 'libs/domain/sector/enum-sectors';
import { SectorService } from '../../services/sector.service';
import { SchemaMembershipStateService, ToastService } from 'libs/core';
import { ButtonComponent, IconComponent } from 'libs/shared';
import { forkJoin } from 'rxjs';
import { IonModal } from '@ionic/angular/standalone';

@Component({
  selector: 'lib-sector-list',
  templateUrl: './sector-list.html',
  standalone: true,
  imports: [CommonModule, SectorListCard, IconComponent, IonModal, ButtonComponent],
  styles: `
    ion-modal.confirm-modal {
      --width: 360px;
      --height: fit-content;
      --border-radius: 12px;
      --box-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1);
      z-index: 99999;
    }
  `,
})
export class SectorList {
  private router = inject(Router);
  route = inject(ActivatedRoute)
  private sectorService = inject(SectorService);
  private schemaState = inject(SchemaMembershipStateService);
  private toastService = inject(ToastService);

  sectors = input<Sector[] | undefined>();

  groupByParent = input<boolean>(false);
  editable = input(false);
  @Output() descriptionsUpdated = new EventEmitter<Array<{ sectorId: number; description: string }>>();

  editingParentId = signal<number | null>(null);
  descriptionDrafts = signal<Map<number, string>>(new Map());
  originalDescriptions = signal<Map<number, string>>(new Map());
  savingParent = signal(false);
  showSaveModal = signal(false);
  showCancelModal = signal(false);
  private pendingSaveItems = signal<Sector[]>([]);
  private pendingEditParent: { parentId: number; items: Sector[] } | null = null;

  readonly sectorList = computed(() => this.sectors() ?? []);

  isSectors = computed(() => this.groupByParent());

  //Agrupa els elements de la llista per parentId
  readonly groupedByParent = computed(() => {
    const arr = this.sectorList();
    const groups: Array<{ parentId: number | null; parentName: string | null, parentDescription: string | undefined, items: Sector[] }> = [];

    for (const s of arr) {
      const pid = s.parentId ?? null;
      const pname = s.parentName ?? null;
      const pdesc = s.parentDescription;
      const last = groups[groups.length - 1];

      if (!last || last.parentId !== pid) {
        groups.push({ parentId: pid, parentName: pname, parentDescription: pdesc, items: [s] });
      } else {
        last.items.push(s);
      }
    }

    return groups;
  });
  
  startEditParent(event: Event, parentId: number, items: Sector[]) {
    event.stopPropagation();
    if (this.editingParentId() !== null && this.editingParentId() !== parentId && this.isGroupDirty()) {
      this.pendingEditParent = { parentId, items };
      this.showCancelModal.set(true);
      return;
    }

    this.openEditParent(parentId, items);
  }

  private openEditParent(parentId: number, items: Sector[]) {
    const drafts = new Map<number, string>();
    for (const s of items) {
      drafts.set(s.id, s.description ?? '');
    }
    this.descriptionDrafts.set(drafts);
    this.originalDescriptions.set(new Map(drafts));
    this.editingParentId.set(parentId);
  }

  isGroupDirty(): boolean {
    const drafts = this.descriptionDrafts();
    const originals = this.originalDescriptions();
    for (const [id, draft] of drafts) {
      if (draft !== (originals.get(id) ?? '')) return true;
    }
    return false;
  }

  cancelEditParent(event: Event) {
    event.stopPropagation();
    if (this.isGroupDirty()) {
      this.showCancelModal.set(true);
    } else {
      this.closeEditing();
    }
  }

  closeCancelModal() {
    this.pendingEditParent = null;
    this.showCancelModal.set(false);
  }

  confirmCancel() {
    const pendingEditParent = this.pendingEditParent;
    this.pendingEditParent = null;
    this.showCancelModal.set(false);
    this.closeEditing();
    if (pendingEditParent) this.openEditParent(pendingEditParent.parentId, pendingEditParent.items);
  }

  private closeEditing() {
    this.editingParentId.set(null);
    this.descriptionDrafts.set(new Map());
    this.originalDescriptions.set(new Map());
  }

  onDescriptionChange(sectorId: number, value: string) {
    const drafts = new Map(this.descriptionDrafts());
    drafts.set(sectorId, value);
    this.descriptionDrafts.set(drafts);
  }

  openSaveModal(event: Event, items: Sector[]) {
    event.stopPropagation();
    this.pendingSaveItems.set(items);
    this.showSaveModal.set(true);
  }

  closeSaveModal() {
    this.showSaveModal.set(false);
  }

  confirmSave() {
    this.closeSaveModal();
    const schemaId = this.schemaState.schema()?.schema_id;
    if (!schemaId) return;

    const items = this.pendingSaveItems();
    const drafts = this.descriptionDrafts();
    const requests = items.map((s) =>
      this.sectorService.updateSectorDescription(
        schemaId,
        s.id,
        drafts.get(s.id) ?? s.description ?? ''
      )
    );

    this.savingParent.set(true);
    forkJoin(requests).subscribe({
      next: () => {
        const updates = items.map((s) => ({
          sectorId: s.id,
          description: drafts.get(s.id) ?? s.description ?? '',
        }));
        this.descriptionsUpdated.emit(updates);
        this.closeEditing();
        this.savingParent.set(false);
        this.toastService.presentToast(
          'success',
          'Descripción guardada',
          '',
          'bottom'
        );
      },
      error: () => {
        this.savingParent.set(false);
        this.toastService.presentToast(
          'error',
          'Error al guardar',
          "No se ha podido guardar la descripción.",
          'bottom'
        );
      },
    });
  }

  goToSector(sector: Sector) {
    if(sector.type == SectorType.SECTOR){
      //this.router.navigate(['/sector', sector.id]);
    }else{
      this.router.navigate([sector.id], { 
        relativeTo: this.route,
        state: sector,
      });      
    }    
  }

}
